import { useEffect, useMemo, useState } from 'react';
import { get } from 'lodash';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { z } from 'zod';
import { ContentGroup } from '@rossum/components/content-group/ContentGroup';
import { Button, Stack } from '@rossum/ui/material';
import { useChangePassword } from '../../../business/user/useChangePassword';
import { usePasswordValidation } from '../../../business/user/usePasswordValidation';
import PasswordValidationField from '../../../components/ReactHookForm/controls/PasswordValidationControl';
import TextFieldControl from '../../../components/ReactHookForm/controls/TextFieldControl';
import { userSelector } from '../../../redux/modules/user/selectors';
import useDebounce from '../../../utils/hooks/useDebounce';

export const ChangePasswordForm = () => {
  const [score, setScore] = useState(0);
  const intl = useIntl();
  const user = useSelector(userSelector);

  const changePasswordSchema = useMemo(
    () =>
      z
        .object({
          oldPassword: z.string().min(1, {
            message: intl.formatMessage({
              id: 'containers.personalInfo.oldPassword.error',
            }),
          }),
          newPassword: z.string().superRefine((_, ctx) => {
            if (score <= 2) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                // returning an empty string fallbacks to default error message `Invalid input`
                // filter out `__SILENT__` to prevent showing error message on the component level
                message: '__SILENT__',
              });
            }
          }),
          confirmPassword: z.string().min(1, {
            message: intl.formatMessage({
              id: 'containers.personalInfo.confirmPassword.error',
            }),
          }),
        })
        .refine(data => data.newPassword === data.confirmPassword, {
          message: intl.formatMessage({
            id: 'components.user.resetPassword.errors.match',
          }),
          path: ['confirmPassword'],
        }),
    [intl, score]
  );

  type ChangePasswordFormValues = z.infer<typeof changePasswordSchema>;

  const defaultValues: ChangePasswordFormValues = {
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  };

  const { handleSubmit, control, setValue, watch, setError, reset } = useForm({
    mode: 'onBlur',
    defaultValues,
    resolver: zodResolver(changePasswordSchema),
  });

  const newPasswordWatcher = watch('newPassword');
  const debounceNewPassword = useDebounce(newPasswordWatcher, 100);

  const { refetch, data } = usePasswordValidation({
    password: debounceNewPassword,
    email: user?.email || user?.username,
  });

  const { mutate: updateUserPassword, error } = useChangePassword();

  useEffect(() => {
    refetch();
  }, [debounceNewPassword, refetch]);

  useEffect(() => {
    if (data?.score) {
      setScore(data.score);
    }
  }, [data?.score, setScore]);

  useEffect(() => {
    const oldPasswordError = get(error, ['data', 'old_password']);
    const newPasswordError = get(error, ['data', 'new_password2', 0]);

    if (oldPasswordError) {
      setError('oldPassword', {
        message: intl.formatMessage({
          id: 'containers.personalInfo.oldPassword.invalid',
        }),
      });
      setValue('oldPassword', '');
    }

    if (newPasswordError) {
      setError('newPassword', {
        message: newPasswordError,
      });
    }
  }, [setValue, setError, intl, error]);

  const onSubmit = (formData: ChangePasswordFormValues) => {
    updateUserPassword(
      {
        new_password1: formData.newPassword,
        new_password2: formData.confirmPassword,
        old_password: formData.oldPassword,
        username: user.username,
      },
      {
        onSuccess: () => reset(),
      }
    );
  };

  return (
    <Stack spacing={4} component="form" onSubmit={handleSubmit(onSubmit)}>
      <ContentGroup
        title={intl.formatMessage({
          id: 'containers.personalInfo.changePassword',
        })}
        description={intl.formatMessage({
          id: 'containers.personalInfo.changePassword.description',
        })}
      >
        <input type="hidden" disabled value={user.username} />

        <TextFieldControl
          type="password"
          autoComplete="off"
          ControllerProps={{ control, name: 'oldPassword' }}
          label={intl.formatMessage({
            id: 'containers.personalInfo.oldPassword.label',
          })}
          placeholder={intl.formatMessage({
            id: 'containers.personalInfo.oldPassword.placeholder',
          })}
          FieldLabelProps={{ layout: 'floating' }}
          inputProps={{
            'data-cy': 'my-profile-current-password-input',
          }}
        />

        <PasswordValidationField
          data={data}
          ControllerProps={{ control, name: 'newPassword' }}
          labelProps={{
            label: intl.formatMessage({
              id: 'containers.personalInfo.newPassword.label',
            }),
            labelLayout: 'floating',
          }}
        />

        <TextFieldControl
          type="password"
          ControllerProps={{ control, name: 'confirmPassword' }}
          autoComplete="new-password"
          label={intl.formatMessage({
            id: 'containers.personalInfo.confirmPassword.label',
          })}
          placeholder={intl.formatMessage({
            id: 'containers.personalInfo.confirmPassword.placeholder',
          })}
          FieldLabelProps={{ layout: 'floating' }}
          inputProps={{
            'data-cy': 'my-profile-confirm-password-input',
          }}
        />

        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            type="submit"
            data-cy="my-profile-update-password"
          >
            {intl.formatMessage({
              id: 'containers.personalInfo.changePassword.button',
            })}
          </Button>
        </Stack>
      </ContentGroup>
    </Stack>
  );
};
